import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiPlus, FiEdit2, FiSearch, FiFilter,
  FiMail, FiPhone, FiFolder,
  FiChevronLeft, FiChevronRight, FiX, FiCheck, // eslint-disable-line no-unused-vars
} from 'react-icons/fi';
import Layout from '../components/Layout';
import PageLoader from '../components/PageLoader';
import API from '../api/axios';
import { toast } from 'react-toastify';
import './Projects2.css';
import './Clients2.css';

const AVATAR_COLORS = ['#3b82f6','#8b5cf6','#10b981','#f59e0b','#ef4444','#06b6d4','#ec4899','#14b8a6'];
const avatarColor = (name) => AVATAR_COLORS[(name?.charCodeAt(0) || 0) % AVATAR_COLORS.length];
const initials    = (n) => n?.split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase() || '?';
const fmt         = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const PAGE_SIZE = 10;


const STATUS_STYLE = {
  active:   { label: 'Active',   color: '#10b981', bg: '#f0fdf4', border: '#bbf7d0' },
  inactive: { label: 'Inactive', color: '#ef4444', bg: '#fef2f2', border: '#fecaca' },
};

const PROJECT_STATUS = {
  planning:    { label: 'Planning',    color: '#8b5cf6', bg: '#f5f3ff', border: '#ddd6fe' },
  in_progress: { label: 'In Progress', color: '#f59e0b', bg: '#fffbeb', border: '#fde68a' },
  review:      { label: 'Review',      color: '#3b82f6', bg: '#eff6ff', border: '#bfdbfe' },
  completed:   { label: 'Completed',   color: '#10b981', bg: '#f0fdf4', border: '#bbf7d0' },
  on_hold:     { label: 'On Hold',     color: '#ef4444', bg: '#fef2f2', border: '#fecaca' },
};

const emptyForm = { name: '', email: '', phone: '', address: '', notes: '', status: 'active' };

export default function Clients() {
  const navigate = useNavigate();
  const [clients, setClients]             = useState([]);
  const [projects, setProjects]           = useState([]);
  const [selected, setSelected]           = useState(null);
  const [clientProjects, setClientProjects] = useState([]);
  const [search, setSearch]               = useState('');
  const [filterStatus, setFilterStatus]   = useState('');
  const [showFilterDrop, setShowFilterDrop] = useState(false);
  const [page, setPage]                   = useState(1);
  const [showModal, setShowModal]         = useState(false);
  const [editing, setEditing]             = useState(null);
  const [form, setForm]                   = useState(emptyForm);
  const [loading, setLoading]             = useState(true);

  const fetchClients = useCallback((selectId = null) => {
    API.get('/clients').then(r => {
      setClients(r.data);
      setSelected(prev => {
        const targetId = selectId || prev?._id;
        if (targetId) return r.data.find(c => c._id === targetId) || r.data[0] || null;
        return r.data[0] || null;
      });
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchClients(); }, [fetchClients]);
  useEffect(() => { API.get('/projects').then(r => setProjects(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    if (selected) {
      setClientProjects(projects.filter(p => p.client?._id === selected._id || p.client === selected._id));
    }
  }, [selected, projects]);

  const filtered = clients.filter(c => {
    const s = search.toLowerCase();
    const matchSearch = !s || c.name?.toLowerCase().includes(s) || c.email?.toLowerCase().includes(s) ||
      c.phone?.includes(s) || c.address?.toLowerCase().includes(s);
    const matchStatus = !filterStatus || c.status === filterStatus;
    return matchSearch && matchStatus;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated  = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const openAdd  = () => { setEditing(null); setForm(emptyForm); setShowModal(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({
      name: c.name, email: c.email || '', phone: c.phone || '',
      address: c.address || '', notes: c.notes || '', status: c.status || 'active',
    });
    setShowModal(true);
  };

  const validate = () => {
    if (!form.name.trim())                          return 'Customer name is required';
    if (form.name.trim().length < 2)               return 'Name must be at least 2 characters';
    if (form.phone && !/^\d{10}$/.test(form.phone.trim()))
                                                    return 'Mobile number must be exactly 10 digits';
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()))
                                                    return 'Please enter a valid email address';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) return toast.error(err);
    try {
      const payload = {
        ...form,
        name:  form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
      };
      let res;
      if (editing) { res = await API.put(`/clients/${editing._id}`, payload); toast.success('Customer updated'); }
      else         { res = await API.post('/clients', payload);               toast.success('Customer added'); }
      setShowModal(false);
      fetchClients(res.data._id);
    } catch (err) { toast.error(err.response?.data?.message || 'Error saving'); }
  };

  const toggleStatus = async (c) => {
    const newStatus = c.status === 'active' ? 'inactive' : 'active';
    try {
      await API.put(`/clients/${c._id}`, { status: newStatus });
      setClients(prev => prev.map(x => x._id === c._id ? { ...x, status: newStatus } : x));
      if (selected?._id === c._id) setSelected({ ...selected, status: newStatus });
      toast.success(`Marked as ${newStatus}`);
    } catch { toast.error('Failed to update status'); }
  };

  const hasFilter = !!filterStatus;
  const selSt    = selected ? (STATUS_STYLE[selected.status] || STATUS_STYLE.active) : null;

  return (
    <Layout>
      {loading && <PageLoader text="Loading customers..." />}
      <div className="pm-root" onClick={() => setShowFilterDrop(false)}>

        {/* ── LEFT PANEL ── */}
        <aside className="pm-sidebar">
          <div className="pm-sidebar-head">
            <div className="pm-sidebar-title-row">
              <h2>Customers</h2>
              <span className="pm-sidebar-count">({filtered.length})</span>
            </div>
            <div className="pm-sidebar-btns">
              <div style={{ position: 'relative' }}>
                <button className={`pm-icon-btn ${hasFilter ? 'pm-icon-btn-active' : ''}`} title="Filter"
                  onClick={e => { e.stopPropagation(); setShowFilterDrop(v => !v); }}>
                  <FiFilter size={14} />
                </button>
                {showFilterDrop && (
                  <div className="pm-filter-drop" style={{ minWidth: 160 }} onClick={e => e.stopPropagation()}>
                    <p className="pm-fd-label">Status</p>
                    <button className={!filterStatus ? 'pm-fd-active' : ''} onClick={() => { setFilterStatus(''); setPage(1); }}>All</button>
                    {Object.entries(STATUS_STYLE).map(([k, v]) => (
                      <button key={k} className={filterStatus === k ? 'pm-fd-active' : ''} style={{ color: v.color }}
                        onClick={() => { setFilterStatus(k); setPage(1); }}>{v.label}</button>
                    ))}
                    {hasFilter && (
                      <>
                        <div className="pm-more-divider" />
                        <button style={{ color: '#dc2626' }} onClick={() => { setFilterStatus(''); setShowFilterDrop(false); }}>
                          <FiX size={11} /> Clear Filter
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
              <button className="pm-new-btn" onClick={openAdd}><FiPlus size={13} /> Add Customer</button>
            </div>
          </div>

          <div className="pm-search-wrap">
            <FiSearch size={13} className="pm-search-icon" />
            <input className="pm-search-input" placeholder="Search customers..."
              value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
          </div>

          <div className="pm-list">
            {paginated.map(c => {
              const st = STATUS_STYLE[c.status] || STATUS_STYLE.active;
              return (
                <div key={c._id} className={`pm-list-item ${selected?._id === c._id ? 'active' : ''}`}
                  onClick={() => setSelected(c)}>
                  <div className="pm-list-thumb" style={{ background: avatarColor(c.name) }}>{initials(c.name)}</div>
                  <div className="pm-list-info">
                    <p className="pm-list-name">{c.name}</p>
                    <p className="pm-list-client">{c.phone || c.email || '—'}</p>
                  </div>
                  <span className="pm-list-badge" style={{ background: st.bg, color: st.color, border: `1px solid ${st.border}` }}>
                    {st.label}
                  </span>
                </div>
              );
            })}
            {paginated.length === 0 && <p className="pm-list-empty">No customers found</p>}
          </div>

          {totalPages > 1 && (
            <div className="pm-pagination">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}><FiChevronLeft size={13} /></button>
              {Array.from({ length: totalPages }, (_, i) => (
                <button key={i} className={page === i + 1 ? 'active' : ''} onClick={() => setPage(i + 1)}>{i + 1}</button>
              ))}
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}><FiChevronRight size={13} /></button>
            </div>
          )}
        </aside>

        {/* ── RIGHT DETAIL ── */}
        {selected ? (
          <main className="pm-detail" onClick={e => e.stopPropagation()}>
            <div className="pm-detail-topbar" style={{ alignItems: 'flex-start' }}>
              <p className="cl-detail-heading">Customer Details</p>
              <div className="pm-detail-actions">
                <button className="pm-action-btn" onClick={() => openEdit(selected)}><FiEdit2 size={13} /> Edit</button>
                <button className="pm-action-btn" style={{ background: '#2563eb', color: '#fff', borderColor: '#2563eb' }}
                  onClick={() => navigate(`/projects?clientId=${selected._id}&clientName=${encodeURIComponent(selected.name)}`)}>
                  <FiPlus size={13} /> New Project
                </button>
              </div>
            </div>

            {/* Profile card */}
            <div className="cl-profile-card">
              <div className="cl-avatar" style={{ background: avatarColor(selected.name) }}>
                {initials(selected.name)}
              </div>
              <div className="cl-profile-info">
                <div className="cl-profile-name-row">
                  <h2>{selected.name}</h2>
                  <span className="pm-status-pill" style={{ background: selSt?.bg, color: selSt?.color, border: `1px solid ${selSt?.border}`, cursor: 'pointer' }}
                    onClick={() => toggleStatus(selected)} title="Click to toggle status">
                    <span className="pm-sdot" style={{ background: selSt?.color }} />{selSt?.label}
                  </span>
                </div>
                <div className="cl-profile-contacts">
                  {selected.phone && <span><FiPhone size={12} /> {selected.phone}</span>}
                  {selected.email && <span><FiMail size={12} /> {selected.email}</span>}
                </div>
              </div>
              {/* Stats */}
              <div className="cl-stat-row">
                <div className="cl-stat"><span>{clientProjects.length}</span><p>Projects</p></div>
                <div className="cl-stat"><span>{clientProjects.filter(p => p.status === 'completed').length}</span><p>Completed</p></div>
                <div className="cl-stat">
                  <span>{clientProjects.reduce((s, p) => s + (p.budget || 0), 0) > 0
                    ? '₹' + (clientProjects.reduce((s, p) => s + (p.budget || 0), 0) / 100000).toFixed(1) + 'L'
                    : '—'}</span>
                  <p>Total Value</p>
                </div>
              </div>
            </div>

            {/* Details */}
            <div className="cl-details-row cl-details-row-single">
              <div className="pm-info-card cl-info-wide">
                <div className="pm-card-header">
                  <h4 className="cl-section-title">Customer Information</h4>
                  <button className="pm-card-edit-btn" onClick={() => openEdit(selected)}><FiEdit2 size={11} /> Edit</button>
                </div>
                <table className="cl-info-tbl">
                  <tbody>
                    <tr><td>Customer Name</td><td>:</td><td><strong>{selected.name}</strong></td></tr>
                    {selected.phone   && <tr><td>Mobile Number</td><td>:</td><td>{selected.phone}</td></tr>}
                    {selected.email   && <tr><td>Email</td><td>:</td><td>{selected.email}</td></tr>}
                    {selected.address && <tr><td>Address</td><td>:</td><td>{selected.address}</td></tr>}
                    <tr><td>Status</td><td>:</td><td>
                      <span className="pm-status-pill" style={{ background: selSt?.bg, color: selSt?.color, border: `1px solid ${selSt?.border}`, fontSize: '.72rem', cursor: 'pointer' }}
                        onClick={() => toggleStatus(selected)}>
                        <span className="pm-sdot" style={{ background: selSt?.color }} />{selSt?.label}
                      </span>
                    </td></tr>
                    <tr><td>Member Since</td><td>:</td><td>{fmt(selected.createdAt)}</td></tr>
                    {selected.notes   && <tr><td>Notes</td><td>:</td><td style={{ color: '#64748b', fontSize: '0.82rem', lineHeight: 1.5 }}>{selected.notes}</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Project History */}
            <div className="pm-info-card">
              <div className="pm-card-header">
                <h4 className="cl-section-title" style={{ display: 'flex', alignItems: 'center', gap: '.5rem' }}>
                  <FiFolder size={15} style={{ color: '#3b82f6' }} /> Project History
                  {clientProjects.length > 0 && <span className="pm-sidebar-count">({clientProjects.length})</span>}
                </h4>
                <button className="pm-card-edit-btn" onClick={() => navigate('/projects')}>
                  View All →
                </button>
              </div>
              {clientProjects.length > 0 ? (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        {['Project ID','Project Name','Site Address','Style','Budget','Status','Start Date','Expected End'].map(h => (
                          <th key={h} style={{ background: '#f8fafc', padding: '.5rem .75rem', textAlign: 'left', fontSize: '.7rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '.04em', borderBottom: '1px solid #f1f5f9', whiteSpace: 'nowrap' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {clientProjects.map(p => {
                        const pst = PROJECT_STATUS[p.status] || PROJECT_STATUS.planning;
                        return (
                          <tr key={p._id} style={{ borderBottom: '1px solid #f8fafc', cursor: 'pointer' }}
                            onClick={() => navigate('/projects')}
                            onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                            onMouseLeave={e => e.currentTarget.style.background = ''}>
                            <td style={{ padding: '.6rem .75rem', fontSize: '.8rem', fontWeight: 600, color: '#3b82f6' }}>
                              {p.projectId || 'PRJ-' + String(p._id).slice(-5).toUpperCase()}
                            </td>
                            <td style={{ padding: '.6rem .75rem', fontSize: '.82rem', color: '#0f172a', fontWeight: 500 }}>{p.title}</td>
                            <td style={{ padding: '.6rem .75rem', fontSize: '.78rem', color: '#64748b' }}>{p.address || '—'}</td>
                            <td style={{ padding: '.6rem .75rem' }}>
                              <span style={{ background: '#f8fafc', color: '#64748b', border: '1px solid #e2e8f0', padding: '.18rem .55rem', borderRadius: 12, fontSize: '.7rem', fontWeight: 600 }}>
                                {p.style || '—'}
                              </span>
                            </td>
                            <td style={{ padding: '.6rem .75rem', fontSize: '.82rem', color: '#0f172a', fontWeight: 500 }}>
                              {p.budget ? `₹ ${Number(p.budget).toLocaleString('en-IN')}` : '—'}
                            </td>
                            <td style={{ padding: '.6rem .75rem' }}>
                              <span style={{ background: pst.bg, color: pst.color, border: `1px solid ${pst.border}`, padding: '.18rem .55rem', borderRadius: 12, fontSize: '.7rem', fontWeight: 700 }}>
                                {pst.label}
                              </span>
                            </td>
                            <td style={{ padding: '.6rem .75rem', fontSize: '.78rem', color: '#64748b' }}>{fmt(p.startDate)}</td>
                            <td style={{ padding: '.6rem .75rem', fontSize: '.78rem', color: '#64748b' }}>{fmt(p.endDate)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#94a3b8' }}>
                  <div style={{ fontSize: '2rem', marginBottom: '.5rem' }}>📁</div>
                  <p style={{ fontSize: '.82rem' }}>No projects yet for this customer</p>
                  <div style={{ display: 'flex', gap: '.5rem', justifyContent: 'center', marginTop: '.75rem' }}>
                    <button className="pm-new-btn" onClick={() => navigate('/projects')}>
                      <FiPlus size={12} /> Create Project
                    </button>
                  </div>
                </div>
              )}
            </div>
          </main>
        ) : (
          <main className="pm-detail pm-no-sel">
            <div style={{ fontSize: '3rem' }}>👥</div>
            <h3>Select a Customer</h3>
            <p>Choose a customer from the panel to view details.</p>
            <button className="pm-new-btn" style={{ marginTop: '1rem' }} onClick={openAdd}><FiPlus size={13} /> Add Customer</button>
          </main>
        )}
      </div>

      {/* ── MODAL ── */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 580 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editing ? 'Edit Customer' : 'Add Customer'}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}><FiX size={14} /></button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>Full Name *</label>
                  <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required placeholder="Customer name" />
                </div>
                <div className="form-group">
                  <label>Mobile Number</label>
                  <input
                    value={form.phone}
                    onChange={e => setForm({ ...form, phone: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                    placeholder="9876543210"
                    inputMode="numeric"
                    maxLength={10}
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Email</label>
                  <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="email@example.com" />
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label>Address</label>
                <input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} placeholder="Customer home/office address" />
              </div>
              <div className="form-group cl-modal-note">
                💡 Site address, project type and budget are set per-project when creating a project for this customer.
              </div>
              <div className="form-group">
                <label>Notes</label>
                <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={3} placeholder="Additional notes..." />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn-primary">{editing ? 'Update Customer' : 'Add Customer'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}
